import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const Navbar = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="glass-nav fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_studio-replica-18/artifacts/pryjb7qe_acad%20studio-Black.png" 
              alt="Acad Studio" 
              className="h-10"
            />
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-10">
            <Link
              to="/"
              data-testid="nav-link-home"
              className={`text-xs font-medium uppercase tracking-wider transition-colors ${
                isActive('/') ? 'text-[#111111]' : 'text-[#5C5C5C] hover:text-[#111111]'
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              data-testid="nav-link-about"
              className={`text-xs font-medium uppercase tracking-wider transition-colors ${
                isActive('/about') ? 'text-[#111111]' : 'text-[#5C5C5C] hover:text-[#111111]'
              }`}
            >
              About
            </Link>

            {/* Services Dropdown */}
            <div className="relative group">
              <button className="text-xs font-medium uppercase tracking-wider text-[#5C5C5C] hover:text-[#111111] transition-colors flex items-center gap-1">
                Services
                <ChevronDown className="w-3 h-3" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-64 backdrop-blur-xl bg-[#F9F8F6]/95 border border-[#D1CCC2]/50 rounded-none py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link
                  to="/services/architecture-design"
                  data-testid="nav-link-architecture"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Architecture Design & Consultancy
                </Link>
                <Link
                  to="/services/interior-design"
                  data-testid="nav-link-interior"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Interior Design & Consultancy
                </Link>
                <Link
                  to="/services/pmc"
                  data-testid="nav-link-pmc"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Project Management & Consultancy
                </Link>
                <a
                  href="https://acadhome.co.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Home Decor
                </a>
              </div>
            </div>

            {/* Portfolio Dropdown - Architecture & Interior only */}
            <div className="relative group">
              <button className="text-xs font-medium uppercase tracking-wider text-[#5C5C5C] hover:text-[#111111] transition-colors flex items-center gap-1">
                Portfolio
                <ChevronDown className="w-3 h-3" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-48 backdrop-blur-xl bg-[#F9F8F6]/95 border border-[#D1CCC2]/50 rounded-none py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link
                  to="/portfolio/architecture"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Architecture Projects
                </Link>
                <Link
                  to="/portfolio/interior"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Interior Projects
                </Link>
              </div>
            </div>
            
            {/* Blog Dropdown */}
            <div className="relative group">
              <button className="text-xs font-medium uppercase tracking-wider text-[#5C5C5C] hover:text-[#111111] transition-colors flex items-center gap-1">
                Blog
                <ChevronDown className="w-3 h-3" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-64 backdrop-blur-xl bg-[#F9F8F6]/95 border border-[#D1CCC2]/50 rounded-none py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link
                  to="/best-architects-in-gurgaon"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Best Architects in Gurgaon
                </Link>
                <Link
                  to="/best-architects-in-delhi-ncr"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Best Architects in Delhi NCR
                </Link>
                <Link
                  to="/best-architects-in-noida"
                  className="block px-4 py-2 text-sm text-[#5C5C5C] hover:text-[#111111] hover:bg-[#EAE6DF]/30"
                >
                  Best Architects in Noida
                </Link>
              </div>
            </div>

            <Link
              to="/contact"
              data-testid="nav-link-contact"
              className={`text-xs font-medium uppercase tracking-wider transition-colors ${
                isActive('/contact') ? 'text-[#111111]' : 'text-[#5C5C5C] hover:text-[#111111]'
              }`}
            >
              Contact
            </Link>

            <Link
              to="/contact"
              data-testid="nav-cta-consultation"
              className="luxury-button"
            >
              Get Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[#5C5C5C]"
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#F9F8F6] border-t border-[#D1CCC2]/50 max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/"
              className="block py-2 text-sm font-medium text-[#5C5C5C]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block py-2 text-sm font-medium text-[#5C5C5C]"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
            
            {/* Services Submenu */}
            <div className="py-2">
              <div className="text-sm font-medium text-[#111111] mb-2">Services</div>
              <div className="pl-4 space-y-2">
                <Link
                  to="/services/architecture-design"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Architecture Design & Consultancy
                </Link>
                <Link
                  to="/services/interior-design"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Interior Design & Consultancy
                </Link>
                <Link
                  to="/services/pmc"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Project Management & Consultancy
                </Link>
                <a
                  href="https://acadhome.co.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home Decor
                </a>
              </div>
            </div>

            {/* Portfolio Submenu - Architecture & Interior only */}
            <div className="py-2">
              <div className="text-sm font-medium text-[#111111] mb-2">Portfolio</div>
              <div className="pl-4 space-y-2">
                <Link
                  to="/portfolio/architecture"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Architecture Projects
                </Link>
                <Link
                  to="/portfolio/interior"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Interior Projects
                </Link>
              </div>
            </div>

            {/* Blog Submenu */}
            <div className="py-2">
              <div className="text-sm font-medium text-[#111111] mb-2">Blog</div>
              <div className="pl-4 space-y-2">
                <Link
                  to="/best-architects-in-gurgaon"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Best Architects in Gurgaon
                </Link>
                <Link
                  to="/best-architects-in-delhi-ncr"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Best Architects in Delhi NCR
                </Link>
                <Link
                  to="/best-architects-in-noida"
                  className="block py-1 text-sm text-[#5C5C5C]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Best Architects in Noida
                </Link>
              </div>
            </div>

            <Link
              to="/contact"
              className="block py-2 text-sm font-medium text-[#5C5C5C]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>

            <Link
              to="/contact"
              className="block w-full bg-[#111111] text-[#F9F8F6] px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-center hover:bg-[#333333] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get Consultation
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
